import { useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

interface MfaBackupCodesProps {
  codes: string[];
  onConfirm: () => void;
  confirming?: boolean;
}

// AC-4: backup codes are shown exactly once; the user must confirm they saved
// them before the session begins.
export default function MfaBackupCodes({
  codes,
  onConfirm,
  confirming = false
}: MfaBackupCodesProps) {
  const [saved, setSaved] = useState(false);

  const copyAll = () => {
    navigator.clipboard?.writeText(codes.join("\n"));
  };

  const download = () => {
    const blob = new Blob(
      [`Suno backup codes — each can be used once.\n\n${codes.join("\n")}\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "suno-backup-codes.txt";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack gap={2} data-testid="mfa-backup-codes">
      <Typography variant="body2" color="text.secondary">
        Store these single-use backup codes somewhere safe. Each one lets you
        sign in once if you can't receive a verification code. They will not be
        shown again.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 1,
          p: 2,
          borderRadius: 1,
          bgcolor: "action.hover",
          fontFamily: "monospace"
        }}
      >
        {codes.map((code) => (
          <Typography key={code} fontFamily="monospace" align="center">
            {code}
          </Typography>
        ))}
      </Box>

      <Stack direction="row" gap={1}>
        <Button variant="outlined" size="small" onClick={copyAll}>
          Copy codes
        </Button>
        <Button variant="outlined" size="small" onClick={download}>
          Download .txt
        </Button>
      </Stack>

      <FormControlLabel
        control={
          <Checkbox
            checked={saved}
            onChange={(event) => setSaved(event.target.checked)}
            data-testid="mfa-backup-codes-saved"
          />
        }
        label="I have saved these codes somewhere safe"
      />

      <LoadingButton
        variant="contained"
        disabled={!saved}
        loading={confirming}
        onClick={onConfirm}
        data-testid="mfa-backup-codes-continue"
      >
        Continue to Suno
      </LoadingButton>
    </Stack>
  );
}
